'use client';

import type { IEnglishIqItemV2 } from '@repo/sdk';
import { Play, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type MediaItemType = Extract<IEnglishIqItemV2, { type: 'audio_choice' | 'video_choice' }>;

interface IEnglishIqV2MediaPlayerProps {
  item: MediaItemType;
  mediaSkipLabel: string;
  onMediaUnavailable: (reason: 'accessibility' | 'data_saver' | 'failure') => void;
  onReplayCountChange: (count: number) => void;
  playLabel: string;
  playsLeftLabel: string;
}

export function EnglishIqV2MediaPlayer({
  item,
  mediaSkipLabel,
  onMediaUnavailable,
  onReplayCountChange,
  playLabel,
  playsLeftLabel,
}: IEnglishIqV2MediaPlayerProps): React.JSX.Element {
  const mediaRef = useRef<HTMLMediaElement>(null);
  const failureSentRef = useRef(false);
  const [completedPlays, setCompletedPlays] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const playsLeft = item.media.replayLimit - completedPlays;

  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } })
      .connection;
    if (connection?.saveData) {
      onMediaUnavailable('data_saver');
    }
  }, [onMediaUnavailable]);

  const setMediaRef = (element: HTMLMediaElement | null): void => {
    mediaRef.current = element;
  };
  const mediaProps = {
    onEnded: () => {
      const nextCount = Math.min(item.media.replayLimit, completedPlays + 1);
      setCompletedPlays(nextCount);
      setIsPlaying(false);
      onReplayCountChange(nextCount);
    },
    onError: () => {
      if (!failureSentRef.current) {
        failureSentRef.current = true;
        onMediaUnavailable('failure');
      }
    },
    onPause: () => setIsPlaying(false),
    onPlay: () => setIsPlaying(true),
    preload: 'metadata' as const,
    src: item.media.url,
  };
  return (
    <div className="mb-6 rounded-2xl border border-[#f7edda]/15 bg-black/25 p-3 sm:p-4">
      {item.type === 'video_choice' ? (
        <video
          {...mediaProps}
          className="aspect-video w-full rounded-xl bg-black object-cover"
          playsInline
          ref={setMediaRef}
        />
      ) : (
        <audio {...mediaProps} ref={setMediaRef} />
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[#f7edda] px-5 font-bold text-[#160c06] disabled:opacity-40"
          disabled={isPlaying || playsLeft <= 0}
          onClick={() => void mediaRef.current?.play()}
          type="button"
        >
          {completedPlays > 0 ? <RotateCcw className="size-4" /> : <Play className="size-4" />}
          {playLabel}
        </button>
        <span aria-live="polite" className="text-sm text-[#f7edda]/65">
          {playsLeftLabel.replace('{count}', String(playsLeft))}
        </span>
        <button
          className="min-h-11 text-sm font-semibold text-[#ffb36b] underline-offset-4 hover:underline"
          onClick={() => onMediaUnavailable('accessibility')}
          type="button"
        >
          {mediaSkipLabel}
        </button>
      </div>
    </div>
  );
}
